import Phaser from 'phaser';
import { CodexManager } from '../systems/CodexManager';
import { MetaManager } from '../systems/MetaManager';
import { CampManager } from '../systems/CampManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ENEMY_DEFINITIONS } from '../constants';
import { CLASS_DEFINITIONS } from '../constants';
import { BASE_SKILLS, CLASS_SKILLS } from '../constants';
import { ITEM_DEFINITIONS, CONSUMABLE_DEFINITIONS, SLOT_LABELS } from '../data/items';
import { EnemyType } from '../types/Combat.types';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DU CODEX / ENCYCLOPÉDIE ===

type CodexTab = 'enemies' | 'weapons' | 'armor' | 'consumables' | 'classes';
const HEADER_H = 85;

const RARITY_COLORS: Record<string, string> = {
  common: '#bdc3c7', uncommon: '#2ecc71', rare: '#f1c40f',
};

// Descriptions lore des ennemis
const ENEMY_LORE: Record<string, string> = {
  zombie: 'Un marin maudit qui refuse de mourir. Dangereux en groupe.',
  brute: 'Un colosse putréfié. Lent mais dévastateur — immobilise ses proies.',
  fast: 'Un fantôme entre deux mondes. Traverse les obstacles.',
  crab: 'Ses pinces ne lâchent jamais. Impossible de fuir son étreinte.',
  gunner: 'Tire à travers tout. Restez hors de sa ligne de mire.',
  siren: 'Son chant soigne les morts-vivants. Tuez-la en priorité.',
  bomber: 'Explose à sa mort. Ne restez pas à côté.',
  cursed: 'Son aura maudite affaiblit tous ceux qui l\'approchent.',
  shark: 'Sent le sang. Plus vous êtes blessé, plus il frappe fort.',
  boss: 'Le commandant de la flotte morte. Son sabre ne pardonne pas.',
  boss_kraken: 'Terreur des profondeurs. Ses tentacules atteignent tout.',
  boss_voodoo: 'Maîtresse des malédictions. Son bâton invoque les pires fléaux.',
};

const ENEMY_PASSIFS: Record<string, string> = {
  zombie: '🧟 Meute : +1 ATK par zombie adjacent à la cible',
  brute: '⛓️ Immobilisation : -1 PM sur la cible touchée',
  crab: '🦀 Étreinte : bloque la fuite des joueurs adjacents',
  shark: '🦈 Flair du sang : +2 ATK contre cibles <50% HP',
  siren: '🧜 Chant de soin : soigne 3 HP à un allié blessé',
  bomber: '💥 Explosion : 2 dégâts zone 3×3 à la mort',
  cursed: '🌊 Aura maudite : -1 ATK joueurs à portée 2 + regen 2 HP/tour',
  fast: '👻 Fantôme : traverse les ennemis',
};

export class CodexScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private codex!: CodexManager;
  private currentTab: CodexTab = 'enemies';
  private returnScene = 'BootScene';
  private returnData: Record<string, unknown> = {};

  constructor() {
    super({ key: 'CodexScene' });
  }

  init(data: { codex?: CodexManager; returnTo?: string; camp?: CampManager; meta?: MetaManager }): void {
    this.codex = data?.codex ?? new CodexManager();
    this.returnScene = data?.returnTo ?? 'BootScene';
    this.returnData = {};
    if (data?.camp) this.returnData.camp = data.camp;
    if (data?.meta) this.returnData.meta = data.meta;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    this.children.removeAll(true);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Header
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);
    this.add.text(GAME_CX, 15, '📖 CODEX', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Compteur global
    const totalEnemies = Object.keys(ENEMY_DEFINITIONS).length;
    const totalItems = Object.keys(ITEM_DEFINITIONS).length;
    const discovered = this.codex.getEnemyCount() + this.codex.getItemCount();
    const total = totalEnemies + totalItems;
    this.add.text(GAME_CX, 38, `Collection : ${discovered}/${total} (${Math.round(discovered / total * 100)}%)`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Bouton retour
    const backBtn = this.add.text(15, 10, '← Retour', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, this.returnData));

    // Onglets
    const tabs: { label: string; short: string; tab: CodexTab }[] = [
      { label: '💀 Ennemis', short: '💀', tab: 'enemies' },
      { label: '⚔️ Armes', short: '⚔️', tab: 'weapons' },
      { label: '🛡️ Équip', short: '🛡️', tab: 'armor' },
      { label: '🧪 Conso', short: '🧪', tab: 'consumables' },
      { label: '👥 Classes', short: '👥', tab: 'classes' },
    ];
    // Sur desktop : 5 × 145 = 580 px, OK dans 800.
    // Sur mobile (450) : on réduit l'espacement et on n'affiche que l'emoji
    // pour que les onglets tiennent dans la largeur.
    const isMobile = GAME_W < 600;
    const tabSpacing = isMobile ? Math.floor((GAME_W - 30) / tabs.length) : 145;
    const tabStartX = GAME_CX - ((tabs.length - 1) * tabSpacing) / 2;
    for (let i = 0; i < tabs.length; i++) {
      const { label, short, tab } = tabs[i];
      const active = this.currentTab === tab;
      const tx = tabStartX + i * tabSpacing;
      const tabBtn = this.add.text(tx, 62, isMobile ? short : label, {
        fontSize: isMobile ? '14px' : '11px', color: active ? '#f1c40f' : '#7f8c8d',
        fontFamily: FONT_BODY, fontStyle: active ? 'bold' : 'normal',
        backgroundColor: active ? '#1a2a40' : '#0a0a1e',
        padding: { x: isMobile ? 6 : 10, y: 5 },
      }).setOrigin(0.5).setDepth(11);
      if (active) this.add.rectangle(tx, 78, tabBtn.width, 2, 0xf1c40f).setDepth(11);
      if (!active) {
        tabBtn.setInteractive({ useHandCursor: true });
        tabBtn.on('pointerdown', () => { this.currentTab = tab; this.drawUI(); });
      }
    }

    // Contenu
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);
    let y = 10;

    switch (this.currentTab) {
      case 'enemies': y = this.drawEnemies(y); break;
      case 'weapons': y = this.drawItems(y, 'weapon'); break;
      case 'armor': y = this.drawArmorAll(y); break;
      case 'consumables': y = this.drawConsumables(y); break;
      case 'classes': y = this.drawClasses(y); break;
    }

    y += 30;
    this.panel.setContentHeight(y);
  }

  // === ENNEMIS ===
  private drawEnemies(y: number): number {
    const allTypes = Object.keys(ENEMY_DEFINITIONS) as EnemyType[];
    const normals = allTypes.filter(t => !t.startsWith('boss'));
    const bosses = allTypes.filter(t => t.startsWith('boss'));
    const cardW = Math.min(560, GAME_W - 20);
    const headerX = GAME_CX - cardW / 2 + 10;

    this.panel.add(this.add.text(headerX, y, `--- Ennemis (${this.codex.getEnemyCount()}/${allTypes.length}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    for (const type of normals) {
      y = this.drawEnemyEntry(type, y);
    }

    y += 10;
    this.panel.add(this.add.text(headerX, y, '--- Boss ---', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
    }));
    y += 22;

    for (const type of bosses) {
      y = this.drawEnemyEntry(type, y);
    }

    return y;
  }

  private drawEnemyEntry(type: string, y: number): number {
    const def = ENEMY_DEFINITIONS[type];
    const discovered = this.codex.isEnemyDiscovered(type);
    const isBoss = type.startsWith('boss');

    // Layout responsive : sur mobile, le passif passe en dessous des stats
    // au lieu de partager la ligne (sinon il chevaucherait le lore).
    const isMobile = GAME_W < 600;
    const cardW = Math.min(560, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 20;
    const passif = ENEMY_PASSIFS[type] ?? '';
    const baseH = isBoss ? 55 : 45;
    const entryH = isMobile && discovered && passif ? baseH + 14 : baseH;
    // Sur desktop : passif à x=430 (= cardRight - 250 quand cardW=560).
    // Sur mobile : ignoré, on l'affiche en dessous.
    const passifX = GAME_CX + cardW / 2 - 250;
    const loreWrapW = isMobile ? cardW - 40 : (passifX - lx - 10);

    const bg = this.add.rectangle(GAME_CX, y + entryH / 2, cardW, entryH, discovered ? 0x1e2d4a : 0x111111);
    bg.setStrokeStyle(1, discovered ? 0x2a3a5c : 0x1a1a1a);
    this.panel.add(bg);

    if (discovered) {
      this.panel.add(this.add.text(lx, y + 5, `${def.emoji} ${def.name}`, {
        fontSize: '12px', color: isBoss ? '#f1c40f' : '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
      }));
      this.panel.add(this.add.text(lx, y + 20, `HP:${def.stats.hp}  ATK:${def.stats.attack}  DEF:${def.stats.defense}  PM:${def.stats.pm}${def.range ? '  PO:' + def.range : ''}`, {
        fontSize: '10px', color: '#8899aa', fontFamily: FONT_BODY,
      }));
      const lore = ENEMY_LORE[type] ?? '';
      if (lore) {
        this.panel.add(this.add.text(lx, y + 32, lore, {
          fontSize: '10px', color: '#8899aa', fontFamily: FONT_BODY,
          wordWrap: { width: loreWrapW },
        }));
      }
      if (passif) {
        if (isMobile) {
          this.panel.add(this.add.text(lx, y + baseH, passif, {
            fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
            wordWrap: { width: cardW - 40 },
          }));
        } else {
          this.panel.add(this.add.text(passifX, y + 5, passif, {
            fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
          }));
        }
      }
    } else {
      this.panel.add(this.add.text(lx, y + 12, '??? — Non rencontré', {
        fontSize: '11px', color: '#6c7a89', fontFamily: FONT_BODY,
      }));
    }

    return y + entryH + 5;
  }

  // === ITEMS (armes) ===
  private drawItems(y: number, slotFilter: string): number {
    const items = Object.values(ITEM_DEFINITIONS).filter(i => i.slot === slotFilter);
    const discoveredCount = items.filter(i => this.codex.isItemDiscovered(i.id)).length;
    const cardW = Math.min(560, GAME_W - 20);
    const headerX = GAME_CX - cardW / 2 + 10;

    this.panel.add(this.add.text(headerX, y, `--- ${SLOT_LABELS[slotFilter] ?? slotFilter} (${discoveredCount}/${items.length}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    for (const item of items) {
      y = this.drawItemEntry(item, y);
    }
    return y;
  }

  private drawItemEntry(item: typeof ITEM_DEFINITIONS[string], y: number): number {
    const discovered = this.codex.isItemDiscovered(item.id);
    const color = discovered ? (RARITY_COLORS[item.rarity] ?? '#ffffff') : '#6c7a89';
    const cardW = Math.min(560, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 20;
    // Bonus à droite — desktop x=400 (= cardRight - 280 quand cardW=560).
    const bonusX = GAME_CX + cardW / 2 - 160;

    const bg = this.add.rectangle(GAME_CX, y + 12, cardW, 28, discovered ? 0x1e2d4a : 0x111111);
    bg.setStrokeStyle(1, discovered ? parseInt(color.replace('#', ''), 16) : 0x1a1a1a);
    this.panel.add(bg);

    if (discovered) {
      this.panel.add(this.add.text(lx, y + 3, `${item.emoji} ${item.name}`, {
        fontSize: '10px', color, fontFamily: FONT_BODY,
      }));
      const bonusParts: string[] = [];
      const b = item.bonuses;
      if (b.attack) bonusParts.push(`ATK${b.attack > 0 ? '+' : ''}${b.attack}`);
      if (b.defense) bonusParts.push(`DEF${b.defense > 0 ? '+' : ''}${b.defense}`);
      if (b.maxHp) bonusParts.push(`HP${b.maxHp > 0 ? '+' : ''}${b.maxHp}`);
      if (b.pa) bonusParts.push(`PA${b.pa > 0 ? '+' : ''}${b.pa}`);
      if (b.pm) bonusParts.push(`PM${b.pm > 0 ? '+' : ''}${b.pm}`);
      if (b.range) bonusParts.push(`PO+${b.range}`);
      if (b.capacity) bonusParts.push(`+${b.capacity} places`);
      this.panel.add(this.add.text(bonusX, y + 5, bonusParts.join('  '), {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    } else {
      this.panel.add(this.add.text(lx, y + 5, '??? — Non découvert', {
        fontSize: '10px', color: '#6c7a89', fontFamily: FONT_BODY,
      }));
    }

    return y + 32;
  }

  // === ARMURES & ACCESSOIRES ===
  private drawArmorAll(y: number): number {
    for (const slot of ['armor', 'belt', 'ring', 'boots', 'backpack'] as const) {
      y = this.drawItems(y, slot);
      y += 10;
    }
    return y;
  }

  // === CONSOMMABLES ===
  private drawConsumables(y: number): number {
    const all = Object.values(CONSUMABLE_DEFINITIONS);
    const isMobile = GAME_W < 600;
    const cardW = Math.min(560, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 20;
    const headerX = GAME_CX - cardW / 2 + 10;
    // Description à x=300 sur desktop (cardRight - 380 quand cardW=560).
    const descX = GAME_CX + cardW / 2 - 380;

    this.panel.add(this.add.text(headerX, y, `--- Consommables (${all.length}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    for (const c of all) {
      // Sur mobile, on stack la description sous le nom (sinon overlap).
      const entryH = isMobile ? 44 : 28;
      const bg = this.add.rectangle(GAME_CX, y + entryH / 2 - 2, cardW, entryH, 0x1e2d4a).setStrokeStyle(1, 0x2a3a5c);
      this.panel.add(bg);
      this.panel.add(this.add.text(lx, y + 3, `${c.emoji} ${c.name}`, {
        fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
      }));
      const dX = isMobile ? lx : descX;
      const dY = isMobile ? y + 18 : y + 5;
      this.panel.add(this.add.text(dX, dY, c.description, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: cardW - (isMobile ? 40 : (descX - (GAME_CX - cardW / 2)) - 10) },
      }));
      y += entryH + 4;
    }
    return y;
  }

  shutdown(): void {
    this.panel?.destroy();
  }

  // === CLASSES ===
  private drawClasses(y: number): number {
    const cardW = Math.min(560, GAME_W - 20);
    const ROW_LEFT = GAME_CX - cardW / 2 + 20;  // 140 desktop, 30 mobile
    const TEXT_W = cardW - 20;                   // 540 desktop, 410 mobile

    for (const cls of ['warrior', 'ranger', 'medic'] as const) {
      const def = CLASS_DEFINITIONS[cls];
      const s = def.stats;

      // Titre + stats : taille fixe (1 ligne chacune)
      const titleText = this.add.text(ROW_LEFT, y + 5, `${def.emoji} ${def.name}`, {
        fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      const statsText = this.add.text(ROW_LEFT, y + 22, `HP:${s.maxHp}  ATK:${s.attack}  DEF:${s.defense}  PA:${s.pa}  PM:${s.pm}  PO:${def.baseRange}`, {
        fontSize: '10px', color: '#8899aa', fontFamily: FONT_BODY,
      });
      // Passif : peut wrap sur 2 lignes pour les classes au passif long
      const passifText = this.add.text(ROW_LEFT, y + 36, `Passif: ${def.passive}`, {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        wordWrap: { width: TEXT_W },
      });

      // Skills de classe — wordWrap pour que les noms longs (Mur de Bouclier,
      // Pluie de Flèches, Purification...) ne débordent pas du cadre.
      const classSkills = CLASS_SKILLS[cls] ?? [];
      const allSkills = [...BASE_SKILLS, ...classSkills];
      const skillNames = allSkills.map(sk => `${sk.emoji}${sk.name}(${sk.paCost}PA)`).join('  ');
      const skillsY = y + 36 + Math.max(passifText.height, 14) + 4;
      const skillsText = this.add.text(ROW_LEFT, skillsY, skillNames, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: TEXT_W },
      });

      // Hauteur dynamique du cadre selon le contenu réel (passif + skills wrappés)
      const contentBottom = skillsY + skillsText.height;
      const boxHeight = Math.max(75, contentBottom - y + 8);
      const bg = this.add.rectangle(GAME_CX, y + boxHeight / 2, cardW, boxHeight, 0x1e2d4a).setStrokeStyle(1, 0x2a3a5c);
      this.panel.add(bg);
      // Remettre les textes au-dessus du cadre (depth)
      [titleText, statsText, passifText, skillsText].forEach(t => {
        this.panel.add(t);
        t.setDepth(1);
      });
      bg.setDepth(0);

      y += boxHeight + 5;
    }
    return y;
  }
}
